import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import { NumberInput } from './NumberInput';
import { colors } from '../theme';
import {
  MarketRentAssumption,
  layoutNameTaken,
  parseSqFt,
  planSquareFeet,
  suggestLayoutName
} from '../utils/floorPlans';

// Mirrors the Unit shape in UnitTable / CreateModel. `import_source` marks the
// units this generator owns: regenerating rewrites those and leaves anything
// hand-entered (or loaded from an older model, where it is null) alone.
interface Unit {
  id: string;
  rent_type: string;
  vacate_flag: number;
  layout: string;
  square_feet: number;
  vacate_month: number;
  current_rent: number | null;
  // Populated by a rent-roll import; null everywhere else. Must stay identical
  // to the Unit shapes in CreateModel / UnitTable.
  unit_label?: string | null;
  lease_end?: string | null;
  market_rent_override?: number | null;
  is_section8?: boolean | null;
  import_source?: string | null;
}

interface UnitMixTableProps {
  units: Unit[];
  setUnits: React.Dispatch<React.SetStateAction<Unit[]>>;
  marketRentAssumptions: MarketRentAssumption[];
  setMarketRentAssumptions: React.Dispatch<React.SetStateAction<MarketRentAssumption[]>>;
  growthRates: { name: string; value: number; type: string; }[];
  modelDetails: any;
}

interface MixRow {
  layout: string;
  count: number;
  avgRent: number;
  rentType: string;
}

const GENERATOR_SOURCE = 'generator';

// A typo in the count field shouldn't be able to spawn a runaway rent roll.
const MAX_UNITS_PER_PLAN = 1000;

let mixIdCounter = 0;
const generateUnitId = () => {
  mixIdCounter += 1;
  return `${Date.now()}-mix-${mixIdCounter}`;
};

// NumberInput hands back a raw string while typing and a number once settled;
// these normalise either into the numbers the rows hold. Keeping row values
// numeric is what lets NumberInput comma-format them.
const toCount = (val: number | string) => {
  const n = Number(String(val ?? '').replace(/,/g, '').trim());
  if (!n || isNaN(n) || n < 0) return 0;
  return Math.min(MAX_UNITS_PER_PLAN, Math.floor(n));
};

// The wizard seeds one blank unit when a model has no rent roll yet — no
// square feet, no rent. It is scaffolding rather than data, so the generator
// clears it out instead of counting it as an existing unit of whichever plan
// it happens to name.
const isPlaceholderUnit = (unit: Unit) =>
  !Number(unit.square_feet ?? 0) &&
  (unit.current_rent === null ||
    unit.current_rent === undefined ||
    Number(unit.current_rent) === 0);

const toMoney = (val: number | string) => {
  const n = Number(String(val ?? '').replace(/,/g, '').trim());
  if (!n || isNaN(n) || n < 0) return 0;
  return Math.round(n);
};

const UnitMixTable: React.FC<UnitMixTableProps> = ({
  units,
  setUnits,
  marketRentAssumptions,
  setMarketRentAssumptions,
  growthRates,
  modelDetails
}) => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const rentTypes = growthRates.filter((r) => r.type === 'rental');
  const defaultRentType = rentTypes[0]?.name || growthRates[0]?.name || '';

  // In-place rents per plan, recomputed from `units`, so the variance note
  // always reflects what the rent roll really holds.
  const rentsByLayout = useMemo(() => {
    const map: { [layout: string]: number[] } = {};
    units.forEach((u) => {
      const rent = Number(u.current_rent ?? 0);
      if (rent <= 0) return;
      (map[u.layout] || (map[u.layout] = [])).push(rent);
    });
    return map;
  }, [units]);

  // Seeded once per mount: flipping into "By unit mix" re-derives counts and
  // average rents from whatever the grid currently holds.
  const [rows, setRows] = useState<MixRow[]>(() =>
    marketRentAssumptions.map((assumption) => {
      const planUnits = units.filter(
        (u) => u.layout === assumption.layout && !isPlaceholderUnit(u)
      );
      const rents = planUnits
        .map((u) => Number(u.current_rent ?? 0))
        .filter((r) => r > 0);
      const avg = rents.length
        ? Math.round(rents.reduce((sum, r) => sum + r, 0) / rents.length)
        : 0;
      return {
        layout: assumption.layout,
        count: planUnits.length,
        avgRent: avg,
        rentType: planUnits[0]?.rent_type || defaultRentType
      };
    })
  );

  const [sqFtDraft, setSqFtDraft] = useState<{ [layout: string]: string }>({});
  const [newPlanName, setNewPlanName] = useState('');
  const [newPlanSqFt, setNewPlanSqFt] = useState('');
  const [pendingRemovals, setPendingRemovals] = useState<{
    added: number;
    refreshed: number;
    removals: { layout: string; count: number; edited: number }[];
    blanks: number;
  } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const updateRow = (layout: string, patch: Partial<MixRow>) => {
    setRows((prev) => prev.map((r) => (r.layout === layout ? { ...r, ...patch } : r)));
  };

  const assumptionFor = (layout: string) =>
    marketRentAssumptions.find((a) => a.layout === layout);

  const rowSqFt = (row: MixRow) => planSquareFeet(assumptionFor(row.layout));

  const setPlanSqFt = (layout: string, raw: string) => {
    setSqFtDraft((prev) => ({ ...prev, [layout]: raw }));
    const next = parseSqFt(raw);
    setMarketRentAssumptions((prev) =>
      prev.map((a) => (a.layout === layout ? { ...a, square_feet: next } : a))
    );
  };

  // Dropping the draft on blur hands display back to the committed number, so
  // the value picks up comma formatting once you leave the field.
  const clearSqFtDraft = (layout: string) => {
    setSqFtDraft((prev) => {
      const copy = { ...prev };
      delete copy[layout];
      return copy;
    });
  };

  // --- Adding a floor plan -------------------------------------------------
  const parsedNewPlanSqFt = parseSqFt(newPlanSqFt);
  const newPlanNameTaken = layoutNameTaken(marketRentAssumptions, newPlanName);
  const suggestedPlanName = newPlanNameTaken
    ? suggestLayoutName(newPlanName, parsedNewPlanSqFt)
    : '';
  const suggestionAvailable =
    !!suggestedPlanName && !layoutNameTaken(marketRentAssumptions, suggestedPlanName);
  const addPlanDisabled = !newPlanName.trim() || newPlanNameTaken;

  const addPlan = () => {
    const layout = newPlanName.trim();
    setMarketRentAssumptions((prev) =>
      prev.concat([{ layout, pf_rent: 0, square_feet: parsedNewPlanSqFt }])
    );
    setRows((prev) =>
      prev.concat([{ layout, count: 0, avgRent: 0, rentType: defaultRentType }])
    );
    setNewPlanName('');
    setNewPlanSqFt('');
  };

  // --- Removing a floor plan ----------------------------------------------
  // Same rule as Edit Layouts: a plan still used by a unit can't be removed,
  // and a model always keeps at least one.
  const planInUse = (layout: string) => units.some((u) => u.layout === layout);

  const removePlan = (layout: string) => {
    setMarketRentAssumptions((prev) => prev.filter((a) => a.layout !== layout));
    setRows((prev) => prev.filter((r) => r.layout !== layout));
  };

  const renderDeletePlanButton = (row: MixRow) => {
    const inUse = planInUse(row.layout);
    const isLast = rows.length <= 1;
    const disabled = inUse || isLast;
    return (
      <Tooltip
        title={
          inUse
            ? 'Cannot delete: used by a unit'
            : isLast
            ? 'Keep at least one floor plan'
            : 'Delete floor plan'
        }
        arrow
      >
        <span>
          <IconButton
            size="small"
            color="error"
            disabled={disabled}
            onClick={() => removePlan(row.layout)}
            aria-label={`Delete ${row.layout}`}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  // --- Totals and capacity -------------------------------------------------
  const totalUnits = rows.reduce((sum, r) => sum + r.count, 0);
  const totalSqFt = rows.reduce((sum, r) => sum + r.count * rowSqFt(r), 0);

  const grossSqFtField = modelDetails?.user_model_field_values?.find(
    (field: any) => field.field_key === 'Gross Square Feet'
  );
  const grossSqFtRaw = grossSqFtField ? Number(grossSqFtField.value) : NaN;
  const grossSqFt = !isNaN(grossSqFtRaw) && grossSqFtRaw > 0 ? grossSqFtRaw : 0;

  const capacityPct = grossSqFt > 0 ? (totalSqFt / grossSqFt) * 100 : null;
  const overCapacity = capacityPct !== null && capacityPct > 100;
  const nearCapacity = capacityPct !== null && capacityPct > 95 && !overCapacity;
  const capacityColor = overCapacity
    ? colors.error
    : nearCapacity
    ? muiTheme.palette.warning.main
    : colors.grey[700];

  // A plan with units but no square feet would strand the user on this step
  // (the wizard needs total SF > 0), so ask for SF before generating.
  const plansMissingSqFt = rows.filter((r) => r.count > 0 && rowSqFt(r) === 0);

  const canGenerate = totalUnits > 0 && !overCapacity && plansMissingSqFt.length === 0;

  // --- Generation ----------------------------------------------------------
  const applyGeneration = () => {
    setUnits((prev) => {
      let next = prev.filter((u) => !isPlaceholderUnit(u));
      rows.forEach((row) => {
        const desired = row.count;
        const sqFt = rowSqFt(row);
        const rent = row.avgRent;

        // Refresh the units this generator owns; hand-entered units keep theirs.
        next = next.map((u) =>
          u.layout === row.layout && u.import_source === GENERATOR_SOURCE
            ? {
                ...u,
                current_rent: rent,
                rent_type: row.rentType,
                square_feet: sqFt > 0 ? sqFt : u.square_feet
              }
            : u
        );

        const existing = next.filter((u) => u.layout === row.layout);
        if (desired > existing.length) {
          const additions: Unit[] = Array.from(
            { length: desired - existing.length },
            () => ({
              id: generateUnitId(),
              rent_type: row.rentType,
              vacate_flag: 0,
              layout: row.layout,
              square_feet: sqFt,
              vacate_month: 0,
              current_rent: rent,
              import_source: GENERATOR_SOURCE
            })
          );
          next = next.concat(additions);
        } else if (desired < existing.length) {
          // Trim from the end of this plan's units.
          const removeIds = existing.slice(desired).map((u) => u.id);
          next = next.filter((u) => removeIds.indexOf(u.id) === -1);
        }
      });
      return next;
    });
  };

  // What generating would do, worked out against the current units. Drives
  // both the removal confirmation and the summary shown afterwards.
  const buildGenerationPlan = () => {
    let added = 0;
    let refreshed = 0;
    const removals: { layout: string; count: number; edited: number }[] = [];

    rows.forEach((row) => {
      const existing = units.filter(
        (u) => u.layout === row.layout && !isPlaceholderUnit(u)
      );
      refreshed += existing.filter((u) => u.import_source === GENERATOR_SOURCE).length;
      if (row.count > existing.length) {
        added += row.count - existing.length;
      } else if (row.count < existing.length) {
        const removed = existing.slice(row.count);
        removals.push({
          layout: row.layout,
          count: removed.length,
          edited: removed.filter((u) => u.import_source !== GENERATOR_SOURCE).length
        });
      }
    });

    return { added, refreshed, removals, blanks: units.filter(isPlaceholderUnit).length };
  };

  const announceGeneration = (plan: ReturnType<typeof buildGenerationPlan>) => {
    const removed = plan.removals.reduce((sum, r) => sum + r.count, 0);
    const parts: string[] = [];
    if (plan.added) parts.push(`${plan.added.toLocaleString()} unit${plan.added === 1 ? '' : 's'} added`);
    if (removed) parts.push(`${removed.toLocaleString()} removed`);
    if (plan.blanks) parts.push(`${plan.blanks.toLocaleString()} blank unit${plan.blanks === 1 ? '' : 's'} cleared`);
    if (!parts.length && plan.refreshed) {
      parts.push(`${plan.refreshed.toLocaleString()} unit${plan.refreshed === 1 ? '' : 's'} refreshed`);
    }
    const total = rows.reduce((sum, r) => sum + r.count, 0);
    setToast(
      parts.length
        ? `Rent roll generated — ${parts.join(', ')}. ${total.toLocaleString()} units total.`
        : `Rent roll already matches this mix — ${total.toLocaleString()} units, nothing to change.`
    );
  };

  const handleGenerate = () => {
    const plan = buildGenerationPlan();
    if (plan.removals.length > 0) {
      setPendingRemovals(plan);
      return;
    }
    applyGeneration();
    announceGeneration(plan);
  };

  const confirmRemovals = () => {
    applyGeneration();
    if (pendingRemovals) announceGeneration(pendingRemovals);
    setPendingRemovals(null);
  };

  const removalList = pendingRemovals?.removals ?? [];
  const totalRemoved = removalList.reduce((sum, r) => sum + r.count, 0);
  const totalRemovedEdited = removalList.reduce((sum, r) => sum + r.edited, 0);

  // --- Rendering helpers ---------------------------------------------------
  // The square-feet field stays mounted whether or not the plan has SF yet;
  // swapping it for static text the moment a value lands would cut typing off
  // after the first digit.
  const renderSqFtCell = (row: MixRow) => {
    const assumption = assumptionFor(row.layout);
    const draft = sqFtDraft[row.layout];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
        {rowSqFt(row) === 0 && (
          <Chip size="small" color="warning" variant="outlined" label="No SF" />
        )}
        <NumberInput
          value={draft !== undefined ? draft : (assumption?.square_feet ?? '')}
          onChange={(val) => setPlanSqFt(row.layout, String(val))}
          placeholder="Set SF"
          size="small"
          variant="standard"
          endAdornment={<InputAdornment position="end">sf</InputAdornment>}
          inputProps={{
            onBlur: () => clearSqFtDraft(row.layout),
            'aria-label': `Square feet for ${row.layout}`
          }}
          sx={{ width: 110 }}
        />
      </Box>
    );
  };

  const renderVarianceNote = (row: MixRow) => {
    const rents = rentsByLayout[row.layout];
    if (!rents || rents.length < 2) return null;
    const min = Math.min.apply(null, rents);
    const max = Math.max.apply(null, rents);
    if (min === max) return null;
    return (
      <Typography variant="caption" sx={{ display: 'block', color: colors.grey[600], mt: 0.25 }}>
        rents vary (${min.toLocaleString()}–${max.toLocaleString()})
      </Typography>
    );
  };

  const renderCountInput = (row: MixRow) => (
    <NumberInput
      value={row.count}
      onChange={(val) => updateRow(row.layout, { count: toCount(val) })}
      size="small"
      variant="outlined"
      fullWidth
      sx={{ '& .MuiInputBase-root': { bgcolor: colors.white } }}
    />
  );

  const renderRentInput = (row: MixRow) => (
    <NumberInput
      value={row.avgRent}
      onChange={(val) => updateRow(row.layout, { avgRent: toMoney(val) })}
      size="small"
      variant="outlined"
      fullWidth
      startAdornment={<InputAdornment position="start">$</InputAdornment>}
      sx={{ '& .MuiInputBase-root': { bgcolor: colors.white } }}
    />
  );

  const renderRentTypeInput = (row: MixRow) => (
    <TextField
      select
      size="small"
      fullWidth
      value={row.rentType}
      onChange={(e) => updateRow(row.layout, { rentType: e.target.value })}
      sx={{ '& .MuiInputBase-root': { bgcolor: colors.white } }}
    >
      {rentTypes.map((rate) => (
        <MenuItem key={rate.name} value={rate.name}>
          {rate.name}
        </MenuItem>
      ))}
    </TextField>
  );

  const gridColumns = 'minmax(0, 1.6fr) 110px minmax(0, 1fr) minmax(0, 1fr) 44px';

  const desktopTable = (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          gap: 1.5,
          px: 1.5,
          py: 0.5,
          mb: 1,
          borderBottom: `1px solid ${colors.grey[300]}`
        }}
      >
        <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Floor Plan</Typography>
        <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}># Units</Typography>
        <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Avg In-Place Rent</Typography>
        <Typography variant="caption" sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Rent Type</Typography>
        <span />
      </Box>
      {rows.map((row) => (
        <Box
          key={row.layout}
          sx={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gap: 1.5,
            alignItems: 'center',
            mb: 1.2,
            px: 1.5,
            py: 1,
            bgcolor: 'grey.100',
            borderRadius: 1
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: colors.grey[900] }}>{row.layout}</Typography>
            {renderSqFtCell(row)}
            {renderVarianceNote(row)}
          </Box>
          {renderCountInput(row)}
          {renderRentInput(row)}
          {renderRentTypeInput(row)}
          {renderDeletePlanButton(row)}
        </Box>
      ))}
    </Box>
  );

  const mobileCards = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {rows.map((row) => (
        <Box
          key={row.layout}
          sx={{ border: `1px solid ${colors.grey[300]}`, borderRadius: 2, p: 2, bgcolor: colors.white }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontWeight: 700, color: colors.grey[900], minWidth: 0 }}>{row.layout}</Typography>
            {renderDeletePlanButton(row)}
          </Box>
          {renderSqFtCell(row)}
          {renderVarianceNote(row)}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
            <Box>
              <Typography variant="caption" sx={{ color: colors.grey[600] }}># Units</Typography>
              {renderCountInput(row)}
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: colors.grey[600] }}>Avg In-Place Rent</Typography>
              {renderRentInput(row)}
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: colors.grey[600] }}>Rent Type</Typography>
              {renderRentTypeInput(row)}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  const addPlanRow = (
    <Box sx={{ mt: 2, px: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: 2
        }}
      >
        <TextField
          label="New floor plan"
          value={newPlanName}
          onChange={(e) => setNewPlanName(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 180 }}
          error={newPlanNameTaken}
          helperText={newPlanNameTaken && !suggestionAvailable ? 'Layout name already exists' : ''}
        />
        <NumberInput
          label="Sq Ft"
          value={newPlanSqFt}
          onChange={(val) => setNewPlanSqFt(String(val))}
          size="small"
          variant="outlined"
          endAdornment={<InputAdornment position="end">sf</InputAdornment>}
          sx={{ width: 130 }}
        />
        <Button variant="outlined" onClick={addPlan} disabled={addPlanDisabled} sx={{ mt: 0.5 }}>
          Add floor plan
        </Button>
      </Box>
      {newPlanNameTaken && suggestionAvailable && (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            "{newPlanName.trim()}" already exists. Add it as "{suggestedPlanName}" instead?
          </Typography>
          <Button size="small" onClick={() => setNewPlanName(suggestedPlanName)}>
            Use this name
          </Button>
        </Box>
      )}
      {newPlanNameTaken && !suggestionAvailable && !parsedNewPlanSqFt && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
          Enter square feet to add a same-named plan at a different size.
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      {isMobile ? mobileCards : desktopTable}

      {addPlanRow}

      <Box
        sx={{
          mt: 2,
          px: 1.5,
          py: 1.5,
          border: `1px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, color: colors.grey[900] }}>
            {totalUnits.toLocaleString()} unit{totalUnits === 1 ? '' : 's'} ·{' '}
            {totalSqFt.toLocaleString()} SF
          </Typography>
          {capacityPct !== null && (
            <Typography variant="body2" sx={{ color: capacityColor, fontWeight: overCapacity || nearCapacity ? 600 : 400 }}>
              {capacityPct.toFixed(1)}% of {grossSqFt.toLocaleString()} GSF ·{' '}
              {totalSqFt <= grossSqFt
                ? `${(grossSqFt - totalSqFt).toLocaleString()} SF remaining`
                : `${(totalSqFt - grossSqFt).toLocaleString()} SF over`}
            </Typography>
          )}
          {plansMissingSqFt.length > 0 && (
            <Typography variant="body2" sx={{ color: muiTheme.palette.warning.main }}>
              Set square feet on {plansMissingSqFt.length} floor plan
              {plansMissingSqFt.length === 1 ? '' : 's'} before generating.
            </Typography>
          )}
          {overCapacity && (
            <Typography variant="body2" sx={{ color: colors.error }}>
              Rentable square feet exceed Gross Square Feet. Reduce unit counts before generating.
            </Typography>
          )}
        </Box>
        <Button variant="contained" onClick={handleGenerate} disabled={!canGenerate}>
          Generate rent roll
        </Button>
      </Box>

      <Dialog open={!!pendingRemovals} onClose={() => setPendingRemovals(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove units?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Generating removes {totalRemoved.toLocaleString()} unit{totalRemoved === 1 ? '' : 's'} from
            the end of {removalList.length === 1 ? 'this floor plan' : 'these floor plans'}:
          </Typography>
          {removalList.map((removal) => (
            <Typography key={removal.layout} variant="body2" sx={{ color: colors.grey[700] }}>
              {removal.layout}: {removal.count.toLocaleString()} unit{removal.count === 1 ? '' : 's'}
            </Typography>
          ))}
          {totalRemovedEdited > 0 && (
            <Typography variant="body2" sx={{ mt: 1.5, color: muiTheme.palette.warning.main }}>
              {totalRemovedEdited.toLocaleString()} of them{' '}
              {totalRemovedEdited === 1 ? 'was' : 'were'} entered or edited individually.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemovals(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmRemovals}>
            Remove and generate
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setToast(null)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UnitMixTable;
